import type * as React from 'react';
import type { ThemeOptions } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import {
  sdsColors,
  sdsGrayscale,
  sdsBaseScales,
  sdsMessageBg,
  sdsRadius,
  sdsBreakpoints,
  sdsSizes,
  sdsFontFamily,
  sdsLineHeight,
  sdsTypeScale,
  sdsFontWeights,
  sdsSpacingUnit,
} from './sds-tokens';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
    big: true;
  }

  interface Palette {
    danger: Palette['primary'];
    brandSenior: Palette['primary'];
    brandSeniorX: Palette['primary'];
  }
  interface PaletteOptions {
    danger?: PaletteOptions['primary'];
    brandSenior?: PaletteOptions['primary'];
    brandSeniorX?: PaletteOptions['primary'];
  }

  interface TypographyVariants {
    pageTitle: React.CSSProperties;
    panelTitle: React.CSSProperties;
    sectionTitle: React.CSSProperties;
    cardsTitle: React.CSSProperties;
    labels: React.CSSProperties;
    inputs: React.CSSProperties;
    helper: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties;
    panelTitle?: React.CSSProperties;
    sectionTitle?: React.CSSProperties;
    cardsTitle?: React.CSSProperties;
    labels?: React.CSSProperties;
    inputs?: React.CSSProperties;
    helper?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
    panelTitle: true;
    sectionTitle: true;
    cardsTitle: true;
    labels: true;
    inputs: true;
    helper: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    danger: true;
  }
}

const derived = {
  primaryHover: '#2A6496',
  primaryActive: '#245682',
  warningDark: '#BD8D02',
  dangerDark: '#C46C06',
  seniorDark: '#00352A',
  primaryLight: '#7EB0DB',
  warningLight: '#FDD35C',
  dangerLight: '#FAB769',
  seniorLight: '#00CEA4',
  seniorXLight: '#FFC74D',
} as const;

const darkNeutral = {
  page: '#121212',
  surface: '#121212',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textDisabled: 'rgba(255, 255, 255, 0.5)',
  border: 'rgba(255, 255, 255, 0.12)',
  inputBorder: 'rgba(255, 255, 255, 0.23)',
  hover: 'rgba(255, 255, 255, 0.08)',
  selected: 'rgba(255, 255, 255, 0.16)',
  chip: '#616161',
  chipHover: '#757575',
  tooltip: 'rgba(97, 97, 97, 0.92)',
} as const;

export function getSdsThemeOptions(mode: PaletteMode = 'light'): ThemeOptions {
  const isDark = mode === 'dark';

  const pageBg = isDark ? darkNeutral.page : sdsGrayscale[5];
  const surface = isDark ? darkNeutral.surface : sdsGrayscale[0];
  const textPrimary = isDark ? sdsGrayscale[0] : sdsColors.textPrimary;
  const textSecondary = isDark ? darkNeutral.textSecondary : sdsColors.textSecondary;
  const borderColor = isDark ? darkNeutral.border : sdsColors.borderTable;
  const inputBorder = isDark ? darkNeutral.inputBorder : sdsGrayscale[30];
  const rowHover = isDark ? darkNeutral.hover : sdsColors.tableHover;
  const rowSelected = isDark ? darkNeutral.selected : sdsColors.selection;
  const cardBorder = isDark ? darkNeutral.border : sdsGrayscale[20];
  const linkColor = isDark ? derived.primaryLight : sdsColors.primary;

  const base = {
    lineHeight: sdsLineHeight,
    letterSpacing: 0,
    fontFamily: sdsFontFamily,
  };

  return {
    breakpoints: { values: sdsBreakpoints },
    spacing: sdsSpacingUnit,
    shape: { borderRadius: sdsRadius.medium },

    palette: {
      mode,

      primary: {
        main: sdsColors.primary,
        dark: derived.primaryHover,
        light: derived.primaryLight,
        contrastText: sdsColors.textLight,
      },
      secondary: {
        main: sdsColors.secondary,
        dark: sdsBaseScales.hippieBlue[700],
        contrastText: sdsColors.textLight,
      },
      success: { main: sdsColors.success, contrastText: sdsColors.textLight },
      info: { main: sdsColors.info, contrastText: sdsColors.textLight },

      error: {
        main: sdsColors.error,
        light: sdsBaseScales.sunsetOrange[300],
        contrastText: sdsColors.textLight,
      },

      warning: {
        main: sdsColors.warning,
        dark: derived.warningDark,
        light: derived.warningLight,
        contrastText: sdsColors.textPrimary,
      },
      danger: {
        main: sdsColors.danger,
        dark: derived.dangerDark,
        light: derived.dangerLight,
        contrastText: sdsColors.textPrimary,
      },
      brandSenior: {
        main: sdsColors.senior,
        dark: derived.seniorDark,
        light: derived.seniorLight,
        contrastText: sdsColors.textLight,
      },
      brandSeniorX: {
        main: sdsColors.seniorX,
        dark: sdsColors.seniorXSecondary,
        light: derived.seniorXLight,
        contrastText: sdsColors.textPrimary,
      },

      text: {
        primary: textPrimary,
        secondary: textSecondary,
        disabled: isDark ? darkNeutral.textDisabled : sdsGrayscale[40],
      },
      background: { default: pageBg, paper: surface },
      divider: borderColor,
      grey: {
        50: sdsGrayscale[5],
        100: sdsGrayscale[10],
        200: sdsGrayscale[20],
        300: sdsGrayscale[30],
        400: sdsGrayscale[40],
        500: sdsGrayscale[50],
        600: sdsGrayscale[60],
        700: sdsGrayscale[70],
        800: sdsGrayscale[80],
        900: sdsGrayscale[90],
      },
    },

    typography: {
      fontFamily: sdsFontFamily,
      fontSize: 14,
      fontWeightRegular: sdsFontWeights.regular,
      fontWeightMedium: sdsFontWeights.semibold,
      fontWeightBold: sdsFontWeights.bold,

      pageTitle: { ...base, ...sdsTypeScale.pageTitle },
      panelTitle: { ...base, ...sdsTypeScale.panelTitle, textTransform: 'uppercase' },
      sectionTitle: { ...base, ...sdsTypeScale.sectionTitle },
      cardsTitle: { ...base, ...sdsTypeScale.cardsTitle },
      labels: { ...base, ...sdsTypeScale.labels },
      inputs: { ...base, ...sdsTypeScale.inputs },
      helper: { ...base, ...sdsTypeScale.helper },

      h1: { ...base, ...sdsTypeScale.pageTitle },
      h2: { ...base, ...sdsTypeScale.panelTitle },
      h3: { ...base, ...sdsTypeScale.sectionTitle },
      h4: { ...base, ...sdsTypeScale.cardsTitle },
      h5: { ...base, ...sdsTypeScale.sectionTitle },
      h6: { ...base, ...sdsTypeScale.sectionTitle },
      subtitle1: { ...base, ...sdsTypeScale.body },
      subtitle2: { ...base, ...sdsTypeScale.sectionTitle },
      body1: { ...base, ...sdsTypeScale.body },
      body2: { ...base, ...sdsTypeScale.body },
      button: { ...base, ...sdsTypeScale.body, textTransform: 'none' },
      caption: { ...base, ...sdsTypeScale.helper },
      overline: { ...base, ...sdsTypeScale.helper, textTransform: 'uppercase' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: sdsFontFamily,
            fontSize: sdsTypeScale.body.fontSize,
            lineHeight: sdsLineHeight,
            color: textPrimary,
            backgroundColor: pageBg,
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            minWidth: sdsSizes.buttonMinWidth,
            minHeight: sdsSizes.inputHeight,
            padding: '8px 12px',
            borderRadius: sdsRadius.medium,
            fontSize: sdsTypeScale.body.fontSize,
            transition: 'background-color 200ms ease-out, color 200ms ease-out',
            '&.Mui-disabled': { opacity: 0.5 },
            '&.Mui-focusVisible': {
              outline: `2px solid ${isDark ? sdsColors.focusRing : derived.primaryActive}`,
              outlineOffset: 2,
            },
          },
          containedPrimary: {
            '&:active': { backgroundColor: derived.primaryActive },
          },
          containedError: {
            backgroundColor: sdsBaseScales.sunsetOrange[700],
            '&:hover': { backgroundColor: sdsBaseScales.sunsetOrange[900] },
          },
          outlined: ({ ownerState }) => ({
            ...(ownerState.color === 'primary' && {
              backgroundColor: surface,
              color: textPrimary,
              borderColor: inputBorder,
              '&:hover': {
                backgroundColor: isDark ? darkNeutral.hover : sdsGrayscale[10],
                borderColor: inputBorder,
              },
            }),
          }),
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: sdsSizes.iconButtonMinWidth,
            borderRadius: sdsRadius.medium,
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            height: sdsSizes.inputHeight,
            borderRadius: sdsRadius.medium,
            backgroundColor: surface,
            fontSize: sdsTypeScale.inputs.fontSize,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: inputBorder },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: inputBorder },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: sdsColors.primary,
              borderWidth: 1,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? sdsBaseScales.sunsetOrange[300] : sdsColors.error,
            },
            '&.Mui-disabled': { opacity: 0.5 },
          },
          input: { padding: '0 10px' },
          multiline: { height: 'auto', minHeight: 70, padding: '8px 10px' },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...sdsTypeScale.labels,
            color: textPrimary,
            '&.Mui-focused': { color: sdsColors.primary },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            ...sdsTypeScale.helper,
            lineHeight: sdsLineHeight,
            letterSpacing: 0,
            marginLeft: 0,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: sdsSizes.tableCellPadding,
            borderBottom: `1px solid ${borderColor}`,
            color: textPrimary,
            fontSize: sdsTypeScale.body.fontSize,
          },
          head: {
            fontWeight: sdsFontWeights.bold,
            backgroundColor: surface,
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: rowHover },
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: rowSelected },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: sdsSizes.tabMinHeight },
          indicator: {
            height: sdsSizes.tabIndicatorHeight,
            backgroundColor: sdsColors.primary,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: sdsSizes.tabMinHeight,
            padding: 8,
            fontSize: sdsTypeScale.body.fontSize,
            color: linkColor,
            fontWeight: sdsFontWeights.regular,
            '&.Mui-selected': {
              color: isDark ? sdsGrayscale[0] : sdsGrayscale[90],
              fontWeight: sdsFontWeights.bold,
            },
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            minHeight: sdsSizes.messageMinHeight,
            padding: sdsSizes.messagePadding,
            borderRadius: sdsRadius.medium,
            borderLeftStyle: 'solid',
            borderLeftWidth: sdsSizes.messageBorderWidth,
            boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)',
            fontSize: sdsTypeScale.body.fontSize,
            alignItems: 'center',
          },
          standardSuccess: {
            borderLeftColor: sdsColors.success,
            ...(isDark ? {} : { backgroundColor: sdsMessageBg.success, color: sdsColors.textPrimary }),
          },
          standardInfo: {
            borderLeftColor: sdsColors.info,
            ...(isDark ? {} : { backgroundColor: sdsMessageBg.info, color: sdsColors.textPrimary }),
          },
          standardWarning: {
            borderLeftColor: sdsColors.warning,
            ...(isDark ? {} : { backgroundColor: sdsMessageBg.warning, color: sdsColors.textPrimary }),
          },
          standardError: {
            borderLeftColor: sdsColors.error,
            ...(isDark ? {} : { backgroundColor: sdsMessageBg.error, color: sdsColors.textPrimary }),
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            borderRadius: sdsRadius.medium,
            fontSize: sdsTypeScale.helper.fontSize,
            minHeight: sdsSizes.badgeHeight,
            ...(ownerState.color === 'default' && {
              backgroundColor: isDark ? darkNeutral.chip : sdsColors.borderChip,
              border: `1px solid ${isDark ? darkNeutral.chip : sdsColors.borderChip}`,
              color: textPrimary,
              '&:hover': {
                backgroundColor: isDark ? darkNeutral.chipHover : sdsColors.chipHover,
              },
            }),
          }),
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: sdsRadius.medium,
            border: `1px solid ${cardBorder}`,
            boxShadow: 'none',
          },
        },
      },

      MuiPaper: {
        styleOverrides: { rounded: { borderRadius: sdsRadius.medium } },
      },

      MuiLink: {
        styleOverrides: { root: { color: linkColor } },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: sdsTypeScale.helper.fontSize,
            backgroundColor: isDark ? darkNeutral.tooltip : sdsGrayscale[90],
            borderRadius: sdsRadius.small,
          },
        },
      },
    },
  };
}

export default getSdsThemeOptions;
